import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTourStore } from '@/stores/tourStore';

/**
 * CheckpointModal — shown at the end of the Tool basic-intro phase. Lets the
 * user pick the full deep-dive tour or jump straight into Zupa.
 *
 * Phase 3b: "Show me everything" navigates to Settings → Workspaces and stores
 * a `pendingPhase`; the Workspaces page picks up that handoff and auto-fires
 * the first deep-dive sub-tour. The deep-dive chain runs itself from there
 * (workspaces → dashboard → … → preferences → EndScreen).
 *
 * Phase 4a: the Workspaces leg is itself four sub-tours (one per modal tab), so
 * the handoff now targets `deep_dive_workspaces_profile` — the first of them.
 *
 * Open/close state is read from `tourStore` (`showCheckpointModal`).
 */
export default function CheckpointModal() {
  const navigate = useNavigate();
  const showCheckpointModal = useTourStore((s) => s.showCheckpointModal);
  const closeCheckpointModal = useTourStore((s) => s.closeCheckpointModal);
  const setPendingPhase = useTourStore((s) => s.setPendingPhase);
  const endTour = useTourStore((s) => s.endTour);

  const finish = () => {
    // Mark the whole tutorial complete so it won't reappear.
    void endTour(true);
    closeCheckpointModal();
  };

  const handleShowEverything = () => {
    // Hand off to the deep-dive chain — the Workspaces page auto-fires it.
    // Mirror chainTo() / handleReplayTour: END the just-finished tool tour FIRST
    // so `currentPhase` is cleared. triggerCheckpoint() left currentPhase at
    // 'tool_intro_post' (it only sets isActive:false), so without this the
    // Workspaces deep-dive trigger's `currentPhase !== null` guard would bail and
    // the deep dive would never start from this entry point. endTour(false)
    // clears the transient tour state without persisting completion (and also
    // closes this modal), exactly as every other deep-dive handoff does.
    // (Phase 4d P0 fix — the CheckpointModal "Show me everything" entry point.)
    void endTour(false);
    setPendingPhase('deep_dive_workspaces_profile');
    navigate('/settings/workspaces');
  };

  return (
    <Dialog
      open={showCheckpointModal}
      onOpenChange={(open) => {
        // Soft dismiss (X / outside click): close + mark complete.
        if (!open) finish();
      }}
    >
      {/* z-index above driver.js's 1e9 popover so this dialog is never behind it. */}
      <DialogContent
        className="max-w-md mx-4 z-[1000000002] bg-white border border-hairline rounded-lg"
        overlayClassName="z-[1000000001]"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-ink text-xl">Got the basics</DialogTitle>
          <DialogDescription className="text-base pt-1 text-steel-dark">
            Want the full tour through workspaces, settings, and the rest?
            Or you good for now?
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-4">
          <Button
            onClick={handleShowEverything}
            size="lg"
            className="w-full bg-cargo hover:bg-cargo/90 text-cargo-ink rounded-md focus-visible:ring-ink"
          >
            Show me everything
          </Button>
          <Button
            onClick={finish}
            size="lg"
            variant="outline"
            className="w-full border-hairline text-steel-dark hover:bg-paper rounded-md"
          >
            I'll take it from here
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
